import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';
import { INDUSTRY_LABEL } from '../lib/industries.js';
import Breadcrumb from '../components/Breadcrumb.jsx';
import SiteForm from '../components/SiteForm.jsx';
import ZipDetail from '../components/ZipDetail.jsx';
import SiteGauges from '../components/Gauges.jsx';
import SiteContext from '../components/SiteContext.jsx';

// Leaflet + tiles only load on a site page.
const SiteMap = lazy(() => import('../components/SiteMap.jsx'));

const REFRESH_MS = 2 * 60 * 1000;
const RANK = { member: 1, admin: 2, owner: 3 };

function fmt(iso, timeZone) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone || undefined,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

const KIND_LABEL = { opened: 'First seen', updated: 'Updated by NWS', cleared: 'Cleared', reopened: 'Active again' };

function Incidents({ incidents, timeZone }) {
  if (!incidents) return null;
  return (
    <section>
      <h3 style={{ marginTop: 22 }}>Alert &amp; flood history at this site</h3>
      {incidents.length === 0 ? (
        <div className="rule-detail">
          No official alerts or gauge flood episodes have affected this site since it was saved. Checked every few
          minutes while the server runs.
        </div>
      ) : (
        incidents.map((i) => (
          <div key={i.id} className={`rule official ${i.status === 'active' ? 'sev-3' : 'sev-0'}`}>
            <div className="rule-head">
              <span className="rule-title">
                {i.source === 'gauge' ? (
                  <span
                    className="tag tag-forecast"
                    title="Calculated by this app from USGS readings against the gauge's NWS flood stages — not an NWS-issued alert"
                  >
                    Gauge · observed
                  </span>
                ) : (
                  <span className="badge-official">Official</span>
                )}{' '}
                {i.event}{' '}
                <span className={`tag ${i.status === 'active' ? 'tag-planning' : ''}`}>{i.status}</span>
              </span>
              <span className="rule-cite">
                {i.evidence?.senderName ?? 'NWS'} · {i.severity}
              </span>
            </div>
            <div className="rule-detail">
              First seen {fmt(i.firstSeen, timeZone)} · last seen {fmt(i.lastSeen, timeZone)}
              {i.clearedAt ? ` · cleared ${fmt(i.clearedAt, timeZone)}` : ''}
              {i.match === 'buffer' ? ' · near the warning area (within buffer)' : ''}
            </div>
            {i.clearReason && <div className="rule-detail muted">{i.clearReason}</div>}
            <ol className="timeline">
              {i.timeline.map((e, idx) => (
                <li key={idx}>
                  {i.source === 'gauge' && e.kind === 'updated' ? 'Level changed' : (KIND_LABEL[e.kind] ?? e.kind)} —{' '}
                  {fmt(e.at, timeZone)}
                </li>
              ))}
            </ol>
            <div className="rule-detail alert-links">
              {i.evidence?.sourceUrl && (
                <a href={i.evidence.sourceUrl} target="_blank" rel="noreferrer">
                  {i.source === 'gauge' ? 'Gauge hydrograph' : 'Latest source alert (NWS API)'}
                </a>
              )}
              {i.source === 'gauge' && i.evidence?.stageFt != null && (
                <span className="muted">
                  Last reading {i.evidence.stageFt} ft{i.evidence.provisional ? ' (provisional)' : ''}
                </span>
              )}
              <span className="muted">Evidence retrieved {fmt(i.evidence?.retrievedAt, timeZone)}</span>
            </div>
          </div>
        ))
      )}
    </section>
  );
}

export default function SiteDetail() {
  const { siteId } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api(`/sites/${siteId}/conditions`);
      setData(d);
      setIncidents(await api(`/sites/${siteId}/incidents`));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [siteId]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (auth.loading) return <div className="app"><div className="loading">Loading…</div></div>;
  if (!auth.user) return <Navigate to="/login" replace />;

  const site = data?.site;
  const role = auth.teams.find((t) => t.id === site?.teamId)?.role;
  const timeZone = data?.conditions?.timezone;

  // ZipDetail's shape: weather/rules from the site's own point, plus the
  // site-level official alerts (exact coordinates + buffer).
  const zipLike =
    data?.conditions && !data.conditions.error
      ? { ...data.conditions, place: site.name, hazards: data.hazards, officialAlerts: data.hazards?.alerts ?? null }
      : null;

  return (
    <div className="app">
      <Breadcrumb
        items={[
          { label: 'My sites', to: site ? `/sites?team=${site.teamId}` : '/sites' },
          { label: site?.name ?? 'Site' },
        ]}
      />
      {error && <div className="banner err">{error}</div>}
      {!data && !error && <div className="loading">Loading site conditions…</div>}

      {site && (
        <>
          <header className="masthead">
            <div>
              <h1>{site.name}</h1>
              <p className="sub">
                {site.lat.toFixed(5)}, {site.lon.toFixed(5)} ·{' '}
                {site.municipality ? `${site.municipality}, ` : ''}
                {site.countyName ?? 'Unknown area'}, {site.state}
                {site.country === 'CA' ? ', Canada' : ''} ·{' '}
                {site.country === 'CA' ? '' : `${INDUSTRY_LABEL[site.industry] ?? site.industry} · `}alert buffer{' '}
                {site.alertBufferM} m
                {site.address ? ` · ${site.address}` : ''}
              </p>
            </div>
            <div className="masthead-actions">
              <button className="btn" onClick={() => setEditing((e) => !e)}>
                {editing ? 'Close editor' : 'Edit site'}
              </button>
              {RANK[role] >= RANK.admin && (
                <button
                  className="btn danger"
                  onClick={async () => {
                    if (!window.confirm(`Delete ${site.name} and its alert history?`)) return;
                    try {
                      await api(`/sites/${site.id}`, { method: 'DELETE' });
                      await auth.refresh();
                      navigate(`/sites?team=${site.teamId}`);
                    } catch (err) {
                      setError(err.message);
                    }
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </header>

          {site.countySource !== 'boundary' && (
            <div className="banner warn">
              This site&rsquo;s {site.country === 'CA' ? 'census division' : 'county'} ({site.countyName}) was
              estimated from the nearest one, because the coordinates fall outside the simplified boundaries
              (often right on a coastline). Area-wide alerts are matched using it, so check it&rsquo;s right.
            </div>
          )}
          {site.notes && <div className="rule-detail site-notes">{site.notes}</div>}

          {editing && (
            <SiteForm
              initial={site}
              submitLabel="Save changes"
              onCancel={() => setEditing(false)}
              onSubmit={async (body) => {
                await api(`/sites/${site.id}`, { method: 'PATCH', body });
                setEditing(false);
                await load();
              }}
            />
          )}

          {data.conditions?.error && (
            <div className="banner err">Couldn&rsquo;t load weather for this site: {data.conditions.error}</div>
          )}
          {zipLike ? (
            <div className="site-detail-panel">
              <ZipDetail zip={zipLike} onClose={() => navigate(`/sites?team=${site.teamId}`)} />
            </div>
          ) : (
            !data.conditions?.error && <div className="loading">Loading conditions…</div>
          )}

          <SiteContext siteId={site.id} />

          <Suspense fallback={<div className="loading">Loading map…</div>}>
            <SiteMap siteId={site.id} refreshKey={`${site.updatedAt}|${(site.gauges ?? []).length}`} />
          </Suspense>

          <SiteGauges
            site={site}
            timeZone={timeZone}
            onSiteChange={(updated) => setData((d) => ({ ...d, site: { ...d.site, ...updated } }))}
          />

          <Incidents incidents={incidents} timeZone={timeZone} />
        </>
      )}
    </div>
  );
}
